import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "../css/jobTracking.css";
import {
  getJob,
  topUpJob,
  verifyPayment,
} from "../services/paymentService";

function formatPrice(amount) {
  return `₦${Number(amount || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(date) {
  if (!date) return "";

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleDateString("en-NG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getStatusLabel(status) {
  switch (status) {
    case "IN_PROGRESS":
      return "In Progress";
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    case "PENDING":
      return "Pending";
    default:
      return status || "Pending";
  }
}

export default function JobTracking() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const paid = searchParams.get("paid");

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  const fetchJob = useCallback(async () => {
    if (!jobId || !token) {
      setError("Invalid tracking link.");
      setLoading(false);
      return;
    }

    try {
      setError("");

      const response = await getJob(jobId, token);

      if (!response?.data?.jobId) {
        throw new Error("Invalid job response.");
      }

      setJob(response.data);
    } catch (err) {
      console.error("Failed to load job:", err);

      if (err.response?.status === 400) {
        setError("This tracking link is invalid or has expired.");
      } else {
        setError("Unable to load your order. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, token]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!jobId || !token) {
        setError("Invalid tracking link.");
        setLoading(false);
        return;
      }

      const reference = sessionStorage.getItem("topupReference");

      if (paid === "true" && reference) {
        try {
          setVerifying(true);
          setError("");

          await verifyPayment(reference);

          sessionStorage.removeItem("topupReference");

          window.history.replaceState(
            {},
            "",
            `${window.location.pathname}?token=${encodeURIComponent(token)}`
          );
        } catch (err) {
          console.error("Top-up verification failed:", err);

          sessionStorage.removeItem("topupReference");

          if (mounted) {
            setError(
              "We could not confirm your payment yet. Please refresh the page."
            );
          }
        } finally {
          if (mounted) {
            setVerifying(false);
          }
        }
      }

      if (mounted) {
        await fetchJob();
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [jobId, token, paid, fetchJob]);

  async function handlePayBalance() {
    if (!jobId || !token || paying) {
      return;
    }

    try {
      setPaying(true);
      setError("");

      const response = await topUpJob(jobId, token);

      const authorizationUrl = response?.data?.authorizationUrl;
      const reference = response?.data?.reference;

      if (!authorizationUrl || !reference) {
        throw new Error("Invalid top-up response.");
      }

      sessionStorage.setItem("topupReference", reference);

      window.location.href = authorizationUrl;
    } catch (err) {
      console.error("Top-up initialization failed:", err);

      if (err.response?.status === 400) {
        setError(
          "Unable to process this payment. Please refresh and try again."
        );
      } else {
        setError("Something went wrong. Please try again.");
      }

      setPaying(false);
    }
  }

  if (loading || verifying) {
    return (
      <main className="job-tracking-page">
        <div className="job-tracking-loading">
          {verifying
            ? "Confirming your payment..."
            : "Loading your order..."}
        </div>
      </main>
    );
  }

  if (error && !job) {
    return (
      <main className="job-tracking-page">
        <div className="job-tracking-error">
          <h1>Unable to load order</h1>

          <p>{error}</p>

          <button type="button" onClick={() => navigate("/")}>
            Back to website
          </button>
        </div>
      </main>
    );
  }

  if (!job) {
    return (
      <main className="job-tracking-page">
        <div className="job-tracking-error">
          <h1>Order not found</h1>

          <p>
            We could not find the order associated with this tracking link.
          </p>

          <button type="button" onClick={() => navigate("/")}>
            Back to website
          </button>
        </div>
      </main>
    );
  }

  const balance = Number(job.balance || 0);
  const amount = Number(job.amount || 0);
  const amountPaid = Number(job.amountPaid || 0);

  return (
    <main className="job-tracking-page">
      <div className="job-tracking-container">
        <header className="job-tracking-header">
          <div>
            <p className="tracking-eyebrow">Order Tracking</p>

            <h1>{job.jobId}</h1>

            <p className="tracking-date">
              Created {formatDate(job.createdAt)}
            </p>
          </div>

          <span
            className={`job-status status-${String(
              job.jobStatus || "PENDING"
            ).toLowerCase()}`}
          >
            {getStatusLabel(job.jobStatus)}
          </span>
        </header>

        {error && <div className="tracking-error">{error}</div>}

        <section className="tracking-card">
          <div className="tracking-card-header">
            <h2>Your Items</h2>

            <span>
              {job.items?.length || 0}{" "}
              {job.items?.length === 1 ? "item" : "items"}
            </span>
          </div>

          <div className="tracking-items">
            {job.items?.length ? (
              job.items.map((item, index) => (
                <div
                  className="tracking-item"
                  key={item.id || `${item.itemName}-${index}`}
                >
                  <div className="tracking-item-main">
                    <h3>{item.itemName}</h3>

                    <div className="tracking-item-details">
                      <span>
                        Quantity: {item.quantity}
                      </span>

                      {item.width != null && item.height != null && (
                        <span>
                          Size: {item.width} × {item.height}{" "}
                          {item.dimensionUnit || "ft"}
                        </span>
                      )}

                      {item.size && (
                        <span>
                          Size: {item.size}
                        </span>
                      )}

                      {item.designRequired && (
                        <span>
                          IGC Design Service
                        </span>
                      )}

                      {item.specifications && (
                        <span>
                          {item.specifications}
                        </span>
                      )}

                      {item.message && (
                        <span>
                          {item.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <strong>
                    {formatPrice(item.amount)}
                  </strong>
                </div>
              ))
            ) : (
              <div className="tracking-empty">
                No items found for this order.
              </div>
            )}
          </div>
        </section>

        <section className="tracking-card payment-card">
          <h2>Payment Summary</h2>

          <div className="payment-row">
            <span>Order total</span>

            <strong>
              {formatPrice(amount)}
            </strong>
          </div>

          <div className="payment-row">
            <span>Amount paid</span>

            <strong>
              {formatPrice(amountPaid)}
            </strong>
          </div>

          <div className="payment-row payment-balance">
            <span>Balance</span>

            <strong>
              {formatPrice(balance)}
            </strong>
          </div>

          {balance > 0 && (
            <button
              type="button"
              className="pay-balance-button"
              onClick={handlePayBalance}
              disabled={paying}
            >
              {paying
                ? "Preparing payment..."
                : `Pay remaining ${formatPrice(balance)}`}
            </button>
          )}

          {balance <= 0 && job.paymentStatus === "PAID" && (
            <div className="payment-complete">
              Payment completed
            </div>
          )}
        </section>
      </div>
    </main>
  );
}